/**
 * Lógica de dados do painel "Score dos Anúncios" — porte de
 * gerar_painel_score.py + painel_template.html (fiesta_party_dados) para TS.
 * Mantém os mesmos pesos, regras e textos de ação (decisão da Mariana, 29/08/2026).
 */

export type RawReview = { a: number; n: number; l: number[]; p?: string }

/** Métricas de uma janela de tempo, geradas por coletar_score_anuncios.py --janelas. */
export type RawWindow = {
  vis?: number
  u?: number
  r?: number
  o?: number
  ped?: number
  canc?: number
  nent?: number
  rec?: number
  clicks?: number
  prints?: number
  ctr?: number
  cvr?: number
  adcost?: number
  adamt?: number
  addir?: number
  acos?: number
  roas?: number
  adunits?: number
}
export type RawPriceCompare = { s: string | null; dif?: number | null; sug?: number | null; cur?: number | null }

export type RawItem = {
  id: string
  acc: 'FESTA' | 'HOME' | string
  title: string
  price?: number
  qty?: number
  soldh?: number
  health?: number
  catalog?: boolean
  full?: boolean
  fship?: boolean
  link?: string
  score?: number
  lvl?: 'good' | 'medium' | 'bad'
  pend?: string[]
  vis?: number
  gauge?: 'healthy' | 'warning' | 'unhealthy' | null
  adst?: string | null
  bbox?: boolean | null
  clicks?: number
  prints?: number
  ctr?: number
  cvr?: number
  roas?: number
  adcost?: number
  acos?: number
  ped?: number
  canc?: number
  nent?: number
  rec?: number
  u30?: number
  r30?: number
  o30?: number
  pc?: RawPriceCompare | null
  rv?: RawReview | null
  /** Métricas por janela ("7", "15", "30", "60", "90"). Ausente nas coletas antigas. */
  w?: Record<string, RawWindow>
}

export type RawData = {
  items: Record<string, RawItem>
  meta: {
    generated_at?: string
    /** Erros de API durante a coleta (401 = token, 403 = permissão). Sem isto o painel mostra
     *  zeros sem explicar — pedido da Mariana, 24/09/2026. */
    erros?: { total: number; por_codigo?: Record<string, number>; amostra?: string[]; sem_visitas?: number }
    contas_coleta?: Record<string, { pedidos_api?: number; pedidos_ok?: boolean; erros_ate_aqui?: number }>
    FESTA?: { user_id: number; nickname: string; rep?: { level_id?: string; power_seller_status?: string } }
    HOME?: { user_id: number; nickname: string; rep?: { level_id?: string; power_seller_status?: string } }
    window?: { orders_from?: string; orders_to?: string; ads_from?: string; ads_to?: string; visits_days?: number }
    /** Uma entrada por janela coletada. Ausente nas coletas antigas (janela única). */
    windows?: Record<string, { orders_from?: string; orders_to?: string; ads_from?: string; ads_to?: string; visits_days?: number }>
    window_default?: string
  }
}

/** Janelas disponíveis no arquivo, em dias. Coleta antiga (sem `windows`) devolve []. */
export function janelasDisponiveis(raw: RawData | null): number[] {
  const ws = raw?.meta?.windows
  if (!ws) return []
  return Object.keys(ws)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)
}

export function janelaPadrao(raw: RawData | null): string {
  const d = raw?.meta?.window_default
  if (d) return d
  const js = janelasDisponiveis(raw)
  return js.length ? String(js.includes(30) ? 30 : js[js.length - 1]) : ''
}

export type Row = {
  id: string
  acc: string
  t: string
  sc: number | null
  lv: string | null
  hl: number | null
  p: number | null
  q: number | null
  sh: number | null
  bbox: boolean | null
  vi: number
  u: number
  r: number
  o: number
  cl: number | null
  pr: number | null
  ct: number | null
  cv: number | null
  ac: number | null
  /** ROAS da publicidade (receita ÷ custo) na janela; null fora do Ads. */
  ro: number | null
  adc: number | null
  ads: string | null
  rv: RawReview | null
  gz: string | null
  pc: RawPriceCompare | null
  pe: number
  cn: number
  ne: number
  rc: number
  cat: boolean
  fs: boolean
  fu: boolean
  lk: string | null
  pd: string[]
  // derivados (painel_template.html linhas 348-373)
  conv: number | null
  np: number
  nota: number | null
  nav: number | null
  ruins: number
  pruins: number | null
  pst: string | null
  caro: boolean
  pdif: number | null
  psug: number | null
  pcur: number | null
  pgap: number | null
  exp: string[]
  pdA: string[]
}

export const RULE_PT: Record<string, string> = {
  TITLE: 'Título',
  SHORTS: 'Vídeo curto (Shorts)',
  PICTURES: 'Fotos',
  GTIN: 'GTIN / EAN',
  TECHNICAL_SPECIFICATIONS_MAIN: 'Ficha técnica',
  STOCK_DEPOSITO: 'Estoque em depósito',
  STOCK_AVAILABILITY_TIME: 'Prazo de disponibilidade',
  ME_FLEX_ITEM_OPTIN: 'Mercado Envios Flex',
  FREE_SHIPPING: 'Frete grátis',
  ADS: 'Publicidade',
  PROMOTIONS: 'Promoções',
  PRICE: 'Preço competitivo',
  FINANCING: 'Parcelamento sem juros',
  FISCAL_DATA: 'Dados fiscais',
  CATALOG: 'Catálogo',
  LEGAL: 'Dados legais',
  SIZE_CHART: 'Guia de tamanhos',
}

export const EXPL: Record<string, [string, string]> = {
  buybox: [
    'Buy box perdido',
    'Anúncio de catálogo em que outro vendedor está ganhando a compra. Quem ganha leva quase toda a visita da página; os demais dividem o resto.',
  ],
  termometro: [
    'Termômetro com problema',
    'Histórico de reclamação e cancelamento derrubou a saúde do anúncio, e o ML reduz a entrega dele na busca.',
  ],
  preco: [
    'Preço acima do mercado',
    'O ML comparou com o mesmo produto em outros vendedores e o nosso está mais caro. Perde posição na busca e elegibilidade a campanha.',
  ],
  hold: [
    'Publicidade desligada',
    'O ML tirou o anúncio da campanha porque ele está pausado ou sem estoque. É problema de operação, não de mídia.',
  ],
  semvisita: [
    'Zero visita em 30 dias',
    'Não recebeu uma única visita no período. Ou está invisível na busca, ou o produto não tem demanda nenhuma.',
  ],
  colapso: [
    'Tráfego despencou',
    'Menos de 30 visitas em 30 dias num anúncio com mais de 50 vendas no histórico. Já vendeu bem e parou de aparecer.',
  ],
  estoque: [
    'Estoque quase no fim',
    'Três unidades ou menos. O ML reduz a entrega de anúncio prestes a ficar sem estoque, e o anúncio sai do ar se zerar.',
  ],
}

/** UP_X e X são a mesma regra (bucket do produto vs bucket do anúncio) — normaliza tirando o prefixo. */
function normRules(pend: string[] | undefined): string[] {
  const out: string[] = []
  for (const p of pend || []) {
    const k = p.startsWith('UP_') ? p.slice(3) : p
    if (!out.includes(k)) out.push(k)
  }
  return out
}

/**
 * Porte de gerar_painel_score.py: raw items → rows com chaves abreviadas + campos derivados.
 *
 * `janela` (em dias, como string) escolhe de qual período vêm visitas, vendas, pedidos,
 * reclamações e métricas de publicidade. Quando o item não tem aquela janela — arquivo de
 * coleta antiga, de janela única — cai nos campos do topo (`vis`, `u30`, `r30`…), que o
 * coletor mantém preenchidos com a janela padrão.
 */
export function buildRows(raw: RawData, janela?: string): Row[] {
  const rows: Row[] = Object.values(raw.items).map((v) => {
    const pd = normRules(v.pend)
    const w: RawWindow | undefined = janela ? v.w?.[janela] : undefined
    const row: Row = {
      id: v.id,
      acc: v.acc,
      t: (v.title || '').slice(0, 90),
      sc: typeof v.score === 'number' ? v.score : null,
      lv: v.lvl ?? null,
      hl: v.health ?? null,
      p: v.price ?? null,
      q: v.qty ?? null,
      sh: v.soldh ?? null,
      bbox: v.bbox ?? null,
      vi: (w ? w.vis : v.vis) || 0,
      u: (w ? w.u : v.u30) || 0,
      r: Math.round(((w ? w.r : v.r30) || 0) * 100) / 100,
      o: (w ? w.o : v.o30) || 0,
      cl: (w ? w.clicks : v.clicks) ?? null,
      pr: (w ? w.prints : v.prints) ?? null,
      ct: (w ? w.ctr : v.ctr) ?? null,
      cv: (w ? w.cvr : v.cvr) ?? null,
      ac: (w ? w.acos : v.acos) ?? null,
      ro: (w ? w.roas : v.roas) ?? null,
      adc: (w ? w.adcost : v.adcost) ?? null,
      ads: v.adst ?? null,
      rv: v.rv ?? null,
      gz: v.gauge ?? null,
      pc: v.pc ?? null,
      pe: (w ? w.ped : v.ped) || 0,
      cn: (w ? w.canc : v.canc) || 0,
      ne: (w ? w.nent : v.nent) || 0,
      rc: (w ? w.rec : v.rec) || 0,
      cat: !!v.catalog,
      fs: !!v.fship,
      fu: !!v.full,
      lk: v.link ?? null,
      pd,
      conv: null,
      np: pd.length,
      nota: null,
      nav: null,
      ruins: 0,
      pruins: null,
      pst: null,
      caro: false,
      pdif: null,
      psug: null,
      pcur: null,
      pgap: null,
      exp: [],
      pdA: [],
    }
    return row
  })
  rows.sort((a, b) => (b.r || 0) - (a.r || 0))

  const DESCARTADAS: Record<string, string> = { FREE_SHIPPING: 'frete grátis', FINANCING: 'parcelamento sem juros' }

  for (const r of rows) {
    r.conv = r.vi > 0 ? (r.u / r.vi) * 100 : null
    r.nota = r.rv && r.rv.n ? r.rv.a : null
    r.nav = r.rv ? r.rv.n : null
    r.ruins = r.rv && r.rv.n ? r.rv.l[0] + r.rv.l[1] : 0
    r.pruins = r.rv && r.rv.n ? (r.ruins / r.rv.n) * 100 : null
    r.pst = r.pc ? r.pc.s : null
    r.caro = !!(r.pc && (r.pc.s === 'with_benchmark_highest' || r.pc.s === 'with_benchmark_high'))
    r.pdif = r.pc?.dif ?? null
    r.psug = r.pc?.sug ?? null
    r.pcur = r.pc?.cur ?? null
    r.pgap = r.pcur !== null && r.psug !== null ? r.pcur - r.psug : null
    r.exp = []
    if (r.cat && r.bbox === false) r.exp.push('buybox')
    if (r.gz === 'unhealthy') r.exp.push('termometro')
    if (r.caro) r.exp.push('preco')
    if (r.ads === 'hold') r.exp.push('hold')
    if (!r.vi) r.exp.push('semvisita')
    else if (r.vi < 30 && (r.sh || 0) >= 50) r.exp.push('colapso')
    if (r.q !== null && r.q > 0 && r.q <= 3) r.exp.push('estoque')
    r.pdA = r.pd.filter((p) => !DESCARTADAS[p])
  }

  return rows
}

export function sum(rows: Row[], f: (r: Row) => number | null | undefined): number {
  return rows.reduce((s, r) => s + (f(r) || 0), 0)
}

export type Pilar = { k: string; p: number; v: number; d: string; real: string }

/** Índice interno da Fiesta: 5 pilares 0–100 com peso fixo. Só "qualidade" vem pronto do ML. */
export function computePilares(rows: Row[]): { pilares: Pilar[]; geral: number } {
  const comScore = rows.filter((r) => typeof r.sc === 'number')
  const scoreMedio = comScore.length ? sum(comScore, (r) => r.sc) / comScore.length : 0
  const comGauge = rows.filter((r) => r.gz)
  const saudaveis = rows.filter((r) => r.gz === 'healthy').length
  const visT = sum(rows, (r) => r.vi)
  const unT = sum(rows, (r) => r.u)
  const pedT = sum(rows, (r) => r.pe)
  const reclT = sum(rows, (r) => r.rc)
  const cancT = sum(rows, (r) => r.cn)
  const avT = sum(rows, (r) => r.nav)
  const estrelas = sum(rows, (r) => (r.rv ? r.rv.l.reduce((s, x, i) => s + x * (i + 1), 0) : 0))
  const nota = avT ? estrelas / avT : 0
  const conv = visT ? (unT / visT) * 100 : 0
  const txRec = pedT ? (reclT / pedT) * 100 : 0
  const txCanc = pedT ? (cancT / pedT) * 100 : 0
  const lim = (v: number) => Math.max(0, Math.min(100, v))
  const CONV_META = 6

  const pilares: Pilar[] = [
    {
      k: 'Qualidade do anúncio',
      p: 0.3,
      v: scoreMedio,
      d: `Média do score do ML entre os ${comScore.length} anúncios que têm score`,
      real: `${scoreMedio.toFixed(1)} / 100`,
    },
    {
      k: 'Experiência de compra',
      p: 0.25,
      v: comGauge.length ? (saudaveis / comGauge.length) * 100 : 0,
      d: `Anúncios com termômetro saudável entre os ${comGauge.length} que têm termômetro`,
      real: `${saudaveis} de ${comGauge.length}`,
    },
    {
      k: 'Satisfação do comprador',
      p: 0.2,
      v: (nota / 5) * 100,
      d: 'Nota média ponderada por número de avaliações, na escala 0–100',
      real: `${nota.toFixed(2)} / 5`,
    },
    {
      k: 'Operação pós-venda',
      p: 0.15,
      v: lim(100 - txRec * 10 - txCanc * 3),
      d: '100 menos 10× a taxa de reclamação e 3× a de cancelamento',
      real: `${txRec.toFixed(2)}% recl. · ${txCanc.toFixed(2)}% canc.`,
    },
    {
      k: 'Conversão',
      p: 0.1,
      v: lim((conv / CONV_META) * 100),
      d: `Unidades por visita contra a referência interna de ${CONV_META}%`,
      real: `${conv.toFixed(2)}% un./visita`,
    },
  ]
  const geral = pilares.reduce((s, x) => s + x.v * x.p, 0)
  return { pilares, geral }
}

export type RecGroup = {
  id: string
  p1?: boolean
  tit: string
  acao: string
  n: number
  receita: number
  top: Row[]
}

type RegraDef = { id: string; p1?: boolean; tit: string; fn: (r: Row) => boolean; acao: string }

const REGRAS: RegraDef[] = [
  {
    id: 'exp',
    p1: true,
    tit: 'Recuperar quem está com experiência de compra ruim',
    fn: (r) => r.gz === 'unhealthy',
    acao: 'Desconto agressivo + campanha de ads para gerar venda boa e limpar o termômetro. É a única alavanca que reverte histórico ruim.',
  },
  {
    id: 'buybox',
    tit: 'Buy box perdido no catálogo',
    fn: (r) => r.exp.includes('buybox'),
    acao: 'Outro vendedor está ganhando a compra no mesmo produto e fica com quase toda a visita da página. Conferir preço, frete e prazo contra o ganhador — o ML informa o preço necessário para virar em /items/{id}/price_to_win.',
  },
  {
    id: 'hold',
    tit: 'Anúncio de ads parado por falta de estoque',
    fn: (r) => r.ads === 'hold',
    acao: 'O ML desligou o anúncio da campanha porque ele está pausado ou sem estoque. Repor estoque devolve o anúncio à mídia — não é problema de campanha.',
  },
  {
    id: 'trafego',
    tit: 'Muita visita e quase nenhuma venda',
    fn: (r) => r.vi >= 300 && (r.conv === null || r.conv < 1),
    acao: 'O tráfego já existe e não vira pedido: revisar preço, primeira foto e título antes de investir mais mídia.',
  },
  {
    id: 'nota',
    tit: 'Nota baixa com avaliação suficiente',
    fn: (r) => (r.nav || 0) >= 5 && r.nota !== null && r.nota <= 3.5,
    acao: 'Nota igual ou abaixo de 3,5 com pelo menos 5 avaliações é problema de produto ou de expectativa na descrição — ler as avaliações antes de mexer em preço.',
  },
  {
    id: 'shorts',
    tit: 'Falta vídeo curto (Shorts)',
    fn: (r) => r.pdA.includes('SHORTS'),
    acao: 'Gravar e subir o vídeo curto. É a pendência de ficha mais comum do catálogo e não custa nada além do tempo de produção.',
  },
  {
    id: 'ficha',
    tit: 'Ficha do produto incompleta',
    fn: (r) =>
      ['TECHNICAL_SPECIFICATIONS_MAIN', 'GTIN', 'PICTURES', 'TITLE', 'FISCAL_DATA', 'SIZE_CHART', 'LEGAL'].some((k) =>
        r.pdA.includes(k),
      ),
    acao: 'Ficha técnica, GTIN, fotos, título ou dados fiscais pendentes. É o bloco que o ML mais cobra no score e é trabalho de cadastro.',
  },
  {
    id: 'promo',
    tit: 'Sem promoção ativa',
    fn: (r) => r.pdA.includes('PROMOTIONS'),
    acao: 'Entrar numa promoção do ML sobe o score e dá exposição. Avaliar desconto que caiba na margem.',
  },
  {
    id: 'ads',
    tit: 'Fora da publicidade',
    fn: (r) => r.pdA.includes('ADS') || r.ads === 'idle',
    acao: 'Anúncio elegível que não está em nenhuma campanha. Subir para uma campanha existente antes de criar campanha nova.',
  },
  {
    id: 'preco',
    tit: 'Preço acima da concorrência',
    fn: (r) => r.caro,
    acao: 'O ML comparou com o mesmo produto de outros vendedores e o nosso está mais caro. Anúncio nessa condição perde posição na busca e elegibilidade a campanha do ML. Baixar até o preço de referência — conferindo antes se a margem aguenta.',
  },
  {
    id: 'estoque',
    tit: 'Anúncio parado e sem estoque',
    fn: (r) => r.q === 0,
    acao: 'Estoque zerado no anúncio ativo. Repor ou encerrar — anúncio ativo sem estoque consome exposição e piora a conversão da conta.',
  },
]

/** Fora da fila por decisão comercial da Mariana (29/08/2026): frete grátis e parcelamento sem juros. */
export const FORA_DA_FILA = 'Frete grátis e parcelamento sem juros — fora da fila por decisão comercial.'

export function computeRecs(rows: Row[]): RecGroup[] {
  return REGRAS.map((g) => {
    const linhas = rows.filter(g.fn)
    return {
      id: g.id,
      p1: g.p1,
      tit: g.tit,
      acao: g.acao,
      n: linhas.length,
      receita: linhas.reduce((s, r) => s + (r.r || 0), 0),
      top: [...linhas].sort((a, b) => (b.r || 0) - (a.r || 0)).slice(0, 3),
    }
  })
    .filter((g) => g.n > 0)
    .sort((a, b) => (a.p1 && !b.p1 ? -1 : b.p1 && !a.p1 ? 1 : b.receita - a.receita))
}

export function computeRegrasPendentes(rows: Row[]): Array<{ k: string; n: number; pt: string }> {
  const counts = new Map<string, number>()
  for (const r of rows) {
    for (const p of r.pd) counts.set(p, (counts.get(p) || 0) + 1)
  }
  return [...counts.entries()]
    .map(([k, n]) => ({ k, n, pt: RULE_PT[k] || k }))
    .sort((a, b) => b.n - a.n)
}

export function filterRecId(rows: Row[], recId: string | null): Row[] {
  if (!recId) return rows
  const def = REGRAS.find((g) => g.id === recId)
  if (!def) return rows
  return rows.filter(def.fn)
}

/* ------------------------------------------------------------------ *
 * Filtros da equipe — porte da barra de filtros do painel_template.html
 * (linhas 259-272) + status de publicidade e Full, pedidos pela Mariana
 * em 13/09/2026. Diferença deliberada em relação ao HTML original: aqui
 * o filtro é GLOBAL — recalcula KPIs, pilares, fila de ações e gráficos,
 * não só a tabela. Foi o pedido: "vários filtros para a equipe atuar".
 * ------------------------------------------------------------------ */

/**
 * Redesenho de 21/09/2026 (pedido da Mariana): o painel é o MAPA — KPIs, pilares, fila de ações e
 * gráficos sempre sobre a base inteira, e TUDO clicável; a tabela "Todos os anúncios" é a LISTA,
 * com os filtros colados nela e os filtros ativos visíveis em chips. Filtro nunca mais recalcula
 * o painel — era isso que confundia.
 */
export type Filtros = {
  conta: string
  nivel: string
  /** Faixa de score "0-40", "40-55", … (barra do gráfico de distribuição). */
  faixa: string
  regra: string
  gauge: string
  exposicao: string
  ads: string
  full: string
  /** Recomendação da fila "O que fazer hoje" (id da regra). */
  rec: string
  /** Estrelas: "1".."5" = anúncios que receberam avaliação com essa nota; "0" = sem nenhuma avaliação. */
  estrelas: string
  soVenda: boolean
  soReclamacao: boolean
  soCancelamento: boolean
  soCaro: boolean
  /** Nota ≤ 3,5 com pelo menos 5 avaliações (pilar "Satisfação do comprador"). */
  notaBaixa: boolean
  busca: string
}

export const FILTROS_VAZIOS: Filtros = {
  conta: '',
  nivel: '',
  faixa: '',
  regra: '',
  gauge: '',
  exposicao: '',
  ads: '',
  full: '',
  rec: '',
  estrelas: '',
  soVenda: false,
  soReclamacao: false,
  soCancelamento: false,
  soCaro: false,
  notaBaixa: false,
  busca: '',
}

export const FAIXAS: Array<[number, number]> = [
  [0, 40],
  [40, 55],
  [55, 70],
  [70, 80],
  [80, 90],
  [90, 101],
]
export const faixaKey = (f: [number, number]) => `${f[0]}-${f[1]}`
export const rotuloFaixa = (f: [number, number]) => (f[0] === 90 ? '90–100' : `${f[0]}–${f[1] - 1}`)
export const faixaDe = (key: string): [number, number] | null => FAIXAS.find((f) => faixaKey(f) === key) ?? null

export const NOME_CONTA: Record<string, string> = { FESTA: 'FIESTAPARTYFESTA', HOME: 'FIESTAPARTYHOME' }

/** Título da regra da fila, para o chip de filtro ativo. */
export const tituloRec = (id: string) => REGRAS.find((g) => g.id === id)?.tit ?? id

/** Chips dos filtros ativos — cada um com a chave para remover só aquele. */
export function descreveFiltros(f: Filtros, diasJanela: number): Array<{ k: keyof Filtros; label: string }> {
  const out: Array<{ k: keyof Filtros; label: string }> = []
  if (f.conta) out.push({ k: 'conta', label: NOME_CONTA[f.conta] || f.conta })
  if (f.nivel) out.push({ k: 'nivel', label: `Nível: ${NIVEL_LABEL[f.nivel] || f.nivel}` })
  if (f.faixa) { const fx = faixaDe(f.faixa); out.push({ k: 'faixa', label: `Score ${fx ? rotuloFaixa(fx) : f.faixa}` }) }
  if (f.gauge) out.push({ k: 'gauge', label: `Termômetro: ${GAUGE_LABEL[f.gauge] || f.gauge}` })
  if (f.regra) out.push({ k: 'regra', label: `Pendência: ${RULE_PT[f.regra] || f.regra}` })
  if (f.exposicao) out.push({ k: 'exposicao', label: `Exposição: ${EXPL[f.exposicao]?.[0] || f.exposicao}` })
  if (f.ads) out.push({ k: 'ads', label: `Publicidade: ${ADS_LABEL[f.ads] || f.ads}` })
  if (f.full) out.push({ k: 'full', label: f.full === 'sim' ? 'Só Full' : 'Sem Full' })
  if (f.rec) out.push({ k: 'rec', label: `Ação: ${tituloRec(f.rec)}` })
  if (f.estrelas) out.push({ k: 'estrelas', label: f.estrelas === '0' ? 'Sem avaliação' : `Com avaliação ${f.estrelas}★` })
  if (f.soVenda) out.push({ k: 'soVenda', label: `Com venda em ${diasJanela}d` })
  if (f.soReclamacao) out.push({ k: 'soReclamacao', label: 'Com reclamação' })
  if (f.soCancelamento) out.push({ k: 'soCancelamento', label: 'Com cancelamento' })
  if (f.soCaro) out.push({ k: 'soCaro', label: 'Acima do mercado' })
  if (f.notaBaixa) out.push({ k: 'notaBaixa', label: 'Nota ≤ 3,5 (5+ avaliações)' })
  if (f.busca.trim()) out.push({ k: 'busca', label: `"${f.busca.trim()}"` })
  return out
}

/** Rótulos dos status de publicidade, com o significado operacional de cada um. */
export const ADS_LABEL: Record<string, string> = {
  active: 'Em campanha (ativo)',
  paused: 'Campanha pausada',
  idle: 'Elegível, fora de campanha',
  hold: 'Desligado por estoque/anúncio pausado',
  sem: 'Sem publicidade',
}

export const GAUGE_LABEL: Record<string, string> = {
  healthy: 'Saudável',
  warning: 'Atenção',
  unhealthy: 'Com problema',
  sem: 'Sem termômetro (venda insuficiente)',
}

export const NIVEL_LABEL: Record<string, string> = {
  good: 'Bom',
  medium: 'Médio',
  bad: 'Ruim',
  sem: 'Sem score (catálogo)',
}

export function aplicaFiltros(rows: Row[], f: Filtros): Row[] {
  return rows.filter((r) => {
    if (f.conta && r.acc !== f.conta) return false
    if (f.nivel) {
      if (f.nivel === 'sem') {
        if (r.sc !== null) return false
      } else if (r.lv !== f.nivel) return false
    }
    if (f.regra && !r.pd.includes(f.regra)) return false
    if (f.gauge) {
      if (f.gauge === 'sem') {
        if (r.gz) return false
      } else if (r.gz !== f.gauge) return false
    }
    if (f.faixa) {
      const fx = faixaDe(f.faixa)
      if (!fx || r.sc === null || r.sc < fx[0] || r.sc >= fx[1]) return false
    }
    if (f.exposicao && !r.exp.includes(f.exposicao)) return false
    if (f.ads) {
      if (f.ads === 'sem') {
        if (r.ads) return false
      } else if (r.ads !== f.ads) return false
    }
    if (f.full === 'sim' && !r.fu) return false
    if (f.full === 'nao' && r.fu) return false
    if (f.rec) {
      const def = REGRAS.find((g) => g.id === f.rec)
      if (def && !def.fn(r)) return false
    }
    if (f.estrelas) {
      if (f.estrelas === '0') { if (r.nav !== 0) return false }
      else { const i = Number(f.estrelas) - 1; if (!r.rv || !(r.rv.l[i] > 0)) return false }
    }
    if (f.soVenda && !r.u) return false
    if (f.soReclamacao && !r.rc) return false
    if (f.soCancelamento && !r.cn) return false
    if (f.soCaro && !r.caro) return false
    if (f.notaBaixa && !((r.nav || 0) >= 5 && r.nota !== null && r.nota <= 3.5)) return false
    const term = f.busca.trim().toLowerCase()
    if (term && !(r.t.toLowerCase().includes(term) || r.id.toLowerCase().includes(term))) return false
    return true
  })
}

export function contaFiltrosAtivos(f: Filtros): number {
  return (Object.keys(FILTROS_VAZIOS) as Array<keyof Filtros>).filter((k) => f[k] !== FILTROS_VAZIOS[k]).length
}

/** Opções do select de motivo de baixa exposição, só com as que existem nos dados. */
export function opcoesExposicao(rows: Row[]): Array<{ k: string; label: string; n: number }> {
  const counts = new Map<string, number>()
  for (const r of rows) for (const k of r.exp) counts.set(k, (counts.get(k) || 0) + 1)
  return [...counts.entries()]
    .map(([k, n]) => ({ k, label: EXPL[k]?.[0] || k, n }))
    .sort((a, b) => b.n - a.n)
}

/** Opções do select de status de publicidade, só com as que existem nos dados. */
export function opcoesAds(rows: Row[]): Array<{ k: string; label: string; n: number }> {
  const counts = new Map<string, number>()
  for (const r of rows) {
    const k = r.ads || 'sem'
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  return [...counts.entries()]
    .map(([k, n]) => ({ k, label: ADS_LABEL[k] || k, n }))
    .sort((a, b) => b.n - a.n)
}
